import { QBS_DETAILS_DATA_TEST_ID } from '@e2e/constants/exam-management';
import { QBankStructureStatus } from '@exam-management/graphql';
import { APP_PERMISSIONS } from '@exam-management/permissions';
import { useQBankStructure } from '@exam-management/qbs-provider';
import {
  type LabelColor,
  Flex,
  IconButton,
  Menu,
  MenuItem,
  ListItemText,
  Text,
  styled,
  ColorPalette,
  CircleIcon,
  ContentCopyRoundedIcon,
  MoreVertIcon,
  EditOutlinedIcon,
  Divider,
} from '@shared/components';
import { useHasPermission, WithPermission } from '@shared/permissions';
import { useState } from 'react';

import { QBankStructureHeaderModalAction, useQBankStructureHeaderModal } from '../../providers';

import { QBANK_STRUCTURE_STATUS_TEXT, QBANK_STRUCTURE_COLOR_STATUS } from './constants';

const StyledMenuSection = styled(Flex)(({ theme }) => ({
  flexDirection: 'column',
  gap: theme.spacing(1),
  width: '100%',
  padding: theme.spacing(2),
}));

export default function QBankStructureHeaderMenu() {
  const { qBankStructureInformation } = useQBankStructure();
  const { setAction } = useQBankStructureHeaderModal();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isOpen = anchorEl !== null;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  function getStatusMenuOptions(status: QBankStructureStatus): {
    label: string;
    color: LabelColor;
    action?: () => void;
  }[] {
    switch (status) {
      case QBankStructureStatus.Draft:
        return [
          {
            label: QBANK_STRUCTURE_STATUS_TEXT[QBankStructureStatus.Published],
            color: QBANK_STRUCTURE_COLOR_STATUS[QBankStructureStatus.Published],
            action: () => setAction(QBankStructureHeaderModalAction.PUBLISH_FROM_DRAFT),
          },
        ];
      case QBankStructureStatus.Published:
        return [
          {
            label: QBANK_STRUCTURE_STATUS_TEXT[QBankStructureStatus.Unpublished],
            color: QBANK_STRUCTURE_COLOR_STATUS[QBankStructureStatus.Unpublished],
            action: () => setAction(QBankStructureHeaderModalAction.UNPUBLISH),
          },
          {
            label: QBANK_STRUCTURE_STATUS_TEXT[QBankStructureStatus.Archived],
            color: QBANK_STRUCTURE_COLOR_STATUS[QBankStructureStatus.Archived],
            action: () => setAction(QBankStructureHeaderModalAction.ARCHIVE),
          },
        ];
      case QBankStructureStatus.Unpublished:
        return [
          {
            label: QBANK_STRUCTURE_STATUS_TEXT[QBankStructureStatus.Published],
            color: QBANK_STRUCTURE_COLOR_STATUS[QBankStructureStatus.Published],
            action: () => setAction(QBankStructureHeaderModalAction.PUBLISH_FROM_UNPUBLISHED),
          },
          {
            label: QBANK_STRUCTURE_STATUS_TEXT[QBankStructureStatus.Archived],
            color: QBANK_STRUCTURE_COLOR_STATUS[QBankStructureStatus.Archived],
            action: () => setAction(QBankStructureHeaderModalAction.ARCHIVE),
          },
        ];
      case QBankStructureStatus.Archived:
        return [
          {
            label: QBANK_STRUCTURE_STATUS_TEXT[QBankStructureStatus.Unpublished],
            color: QBANK_STRUCTURE_COLOR_STATUS[QBankStructureStatus.Unpublished],
            action: () => setAction(QBankStructureHeaderModalAction.UNPUBLISH),
          },
        ];
      default:
        return [];
    }
  }

  const actionMenuOptions = [
    {
      label: 'Edit Information',
      IconComponent: EditOutlinedIcon,
      action: () => setAction(QBankStructureHeaderModalAction.EDIT_INFORMATION),
      requiredPermission: APP_PERMISSIONS.QBANK.STRUCTURE.UPDATE,
    },
    {
      label: 'Duplicate',
      IconComponent: ContentCopyRoundedIcon,
      action: () => setAction(QBankStructureHeaderModalAction.DUPLICATE),
      requiredPermission: APP_PERMISSIONS.QBANK.STRUCTURE.CREATE,
    },
  ];

  const isShownMenu = useHasPermission(
    [
      APP_PERMISSIONS.QBANK.STRUCTURE.CREATE,
      APP_PERMISSIONS.QBANK.STRUCTURE.UPDATE,
      APP_PERMISSIONS.QBANK.STRUCTURE.STATUS,
    ],
    'OR',
  );

  return (
    <>
      {isShownMenu ? (
        <IconButton
          data-testid={QBS_DETAILS_DATA_TEST_ID.HEADER_MENU}
          sx={(theme) => ({
            borderRadius: theme.shape.rounded(1),
            border: `1px solid ${theme.palette.grey[400]}`,
          })}
          onClick={handleClick}
        >
          <MoreVertIcon />
        </IconButton>
      ) : null}
      <Menu
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          mt: (theme) => theme.spacing(0.5),
        }}
        MenuListProps={{
          sx: (theme) => ({
            borderRadius: theme.shape.rounded(2),
            width: '180px',
            p: theme.spacing(0),
          }),
        }}
      >
        <WithPermission requiredPermission={APP_PERMISSIONS.QBANK.STRUCTURE.STATUS}>
          <StyledMenuSection>
            <Text
              variant='caption'
              sx={(theme) => ({
                fontWeight: theme.typography.fontWeightMedium,
                px: theme.spacing(2),
              })}
            >
              Update Status to
            </Text>
            {getStatusMenuOptions(qBankStructureInformation.status).map(
              ({ label, color, action }) => (
                <MenuItem
                  data-testid={label.toLowerCase()}
                  key={label}
                  onClick={() => {
                    handleClose();
                    action?.();
                  }}
                  sx={{
                    '&:hover': {
                      backgroundColor: (theme) => theme.palette.primary.lighter,
                    },
                  }}
                >
                  <CircleIcon
                    sx={(theme) => ({
                      color: Object.values(ColorPalette).includes(color as ColorPalette)
                        ? theme.palette[color as ColorPalette].main
                        : color,
                      width: '10px',
                      height: '10px',
                      mr: theme.spacing(2),
                    })}
                  />
                  <ListItemText>{label}</ListItemText>
                </MenuItem>
              ),
            )}
          </StyledMenuSection>
        </WithPermission>

        <Divider />
        <StyledMenuSection>
          <Text
            variant='caption'
            sx={(theme) => ({
              fontWeight: theme.typography.fontWeightMedium,
              px: theme.spacing(2),
            })}
          >
            Actions
          </Text>
          {actionMenuOptions.map(({ label, IconComponent, action, requiredPermission }) => (
            <WithPermission requiredPermission={requiredPermission} key={label}>
              <MenuItem
                data-testid={label.toLowerCase()}
                onClick={() => {
                  handleClose();
                  action();
                }}
                sx={{
                  '&:hover': {
                    backgroundColor: (theme) => theme.palette.primary.lighter,
                  },
                }}
              >
                <IconComponent
                  sx={(theme) => ({
                    width: '24px',
                    height: '24px',
                    mr: theme.spacing(2),
                    color: theme.palette.grey[600],
                  })}
                />
                <ListItemText>{label}</ListItemText>
              </MenuItem>
            </WithPermission>
          ))}
        </StyledMenuSection>
      </Menu>
    </>
  );
}
